import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import axios from "axios";
import { BASE_URL } from "../config";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import BILLING from "../api/services/BILLING";
import { paths } from "../routes/paths";
import styles from "../styles/_screens/purchases.module.scss";

const FILTER_TABS = [
  { key: "ALL", label: "הכל" },
  { key: "PAID", label: "שולם" },
  { key: "DEBT", label: "חוב" },
  { key: "FREE", label: "חינם" },
];

const PAGE_SIZE = 20;

export default function Purchases() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const debounceRef = useRef(null);

  // ── Fetch purchases ──
  const fetchPurchases = useCallback(
    async (currentPage, currentFilter, currentSearch, append = false) => {
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = {
          page: currentPage,
          size: PAGE_SIZE,
          search: currentSearch || undefined,
          filter: currentFilter !== "ALL" ? currentFilter : undefined,
        };
        const res = await BILLING.get_business_purchases(params);
        const data = res?.data;
        const items = Array.isArray(data)
          ? data
          : data?.items || data?.purchases || data?.data || [];
        const total = data?.total;

        if (append) {
          setPurchases((prev) => [...prev, ...items]);
        } else {
          setPurchases(items);
        }

        if (total != null) {
          setHasMore(currentPage * PAGE_SIZE < total);
        } else {
          setHasMore(items.length >= PAGE_SIZE);
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05E8\u05DB\u05D9\u05E9\u05D5\u05EA"
        );
        if (!append) setPurchases([]);
      }
      setLoading(false);
      setLoadingMore(false);
    },
    []
  );

  // Trigger fetch on filter / page changes
  useEffect(() => {
    if (page === 1) {
      fetchPurchases(1, filter, search, false);
    } else {
      fetchPurchases(page, filter, search, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPurchases(1, filter, search, false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ── Filter change handler ──
  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;
    setFilter(newFilter);
    setPage(1);
  };

  // ── Load more ──
  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  // ── Helpers ──
  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm");
    } catch {
      return dateStr || "-";
    }
  };

  const ITEM_TYPE_LABELS = {
    plan: { label: "מנוי", cls: styles.typeSubscription },
    product: { label: "מוצר", cls: styles.typePunchcard },
    service: { label: "שירות", cls: styles.typeSubscription },
    custom: { label: "פריט", cls: styles.typePunchcard },
    appointment: { label: "תור", cls: styles.typeSubscription },
  };

  const PAYMENT_METHOD_LABELS = {
    credit: "אשראי",
    cash: "מזומן",
    bank: "העברה",
    check: "צ'ק",
    debt: "חוב",
    other: "אחר",
  };

  const getPlanType = (purchase) => {
    // New POS items have itemType
    if (purchase.itemType) {
      return ITEM_TYPE_LABELS[purchase.itemType] || { label: purchase.itemType, cls: "" };
    }
    // Legacy plan purchases
    const plan = purchase.userServicePlanId;
    if (plan?.isSubscription) {
      return { label: "מנוי חודשי", cls: styles.typeSubscription };
    }
    return { label: "כרטיסיה", cls: styles.typePunchcard };
  };

  const getStatus = (purchase) => {
    if (purchase.status === "PENDING") return { label: "חוב", className: styles.statusExpired };
    if (purchase.status === "COMPLETED") return { label: "שולם", className: styles.statusActive };
    if (purchase.status === "REFUNDED") return { label: "הוחזר", className: styles.statusFrozen };
    // Fallback: check plan
    const plan = purchase.userServicePlanId;
    if (plan?.isFrozen) return { label: "מוקפא", className: styles.statusFrozen };
    if (plan && !plan.isActive) return { label: "פג תוקף", className: styles.statusExpired };
    return { label: "שולם", className: styles.statusActive };
  };

  const getAmount = (purchase) => {
    const amount = purchase.amount ?? 0;
    if (amount === 0) return { text: "חינם", isFree: true };
    return { text: `₪${amount}`, isFree: false };
  };

  // Mark debt as paid
  const markAsPaid = async (paymentId) => {
    try {
      const { data } = await axios.put(
        `${BASE_URL}/pos/mark-paid/${paymentId}`,
        {},
        { headers: { Authorization: JSON.parse(localStorage.getItem("token") || "null") } }
      );
      if (data.success) {
        toast.success("סומן כשולם");
        fetchPurchases(1, filter, search, false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה");
    }
  };

  // ── Loading state ──
  if (loading && purchases.length === 0) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>
            <div className={styles.tableContainer}>
              {/* Header */}
              <div className={styles.header}>
                <h2>{"\u05E8\u05DB\u05D9\u05E9\u05D5\u05EA"}</h2>
              </div>

              {/* Filter tabs */}
              <div className={styles.tabs}>
                {FILTER_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.tab} ${filter === tab.key ? styles.tabActive : ""}`}
                    onClick={() => handleFilterChange(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className={styles.searchRow}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={"\u05D7\u05D9\u05E4\u05D5\u05E9 \u05DC\u05E4\u05D9 \u05E9\u05DD \u05DC\u05E7\u05D5\u05D7 \u05D0\u05D5 \u05D8\u05DC\u05E4\u05D5\u05DF..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  dir="rtl"
                />
              </div>

              {/* Table */}
              {purchases.length === 0 ? (
                <div className={styles.emptyState}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 01-8 0" />
                  </svg>
                  <p>{"\u05D0\u05D9\u05DF \u05E8\u05DB\u05D9\u05E9\u05D5\u05EA"}</p>
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>תאריך</th>
                        <th>לקוח</th>
                        <th>פריט</th>
                        <th>סוג</th>
                        <th>סכום</th>
                        <th>אמצעי תשלום</th>
                        <th>סטטוס</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map((purchase) => {
                        const planType = getPlanType(purchase);
                        const status = getStatus(purchase);
                        const amount = getAmount(purchase);

                        return (
                          <tr
                            key={purchase._id}
                            className={styles.tableRow}
                            onClick={() => {
                              const clientId = purchase.userId?._id || purchase.userId;
                              if (clientId && typeof clientId === "string") {
                                navigate(
                                  `${paths.clientBilling(clientId)}?tab=purchases`
                                );
                              }
                            }}
                          >
                            <td data-label="תאריך">
                              {formatDate(purchase.createdAt)}
                            </td>
                            <td data-label="לקוח">
                              <div className={styles.customerCell}>
                                <span className={styles.customerName}>
                                  {purchase.userId?.name || "-"}
                                </span>
                                <span className={styles.customerPhone}>
                                  {purchase.userId?.phone || ""}
                                </span>
                              </div>
                            </td>
                            <td data-label="פריט">
                              {purchase.description || purchase.userServicePlanId?.name || "-"}
                            </td>
                            <td data-label="סוג">
                              <span className={`${styles.typeBadge} ${planType.cls || planType.className || ""}`}>
                                {planType.label}
                              </span>
                            </td>
                            <td data-label="סכום">
                              <span className={amount.isFree ? styles.amountFree : ""}>
                                {amount.text}
                              </span>
                            </td>
                            <td data-label="אמצעי תשלום">
                              {PAYMENT_METHOD_LABELS[purchase.paymentMethod] || purchase.paymentMethod || "אשראי"}
                            </td>
                            <td data-label="סטטוס">
                              <span className={`${styles.statusBadge} ${status.className}`}>
                                {status.label}
                              </span>
                            </td>
                            <td data-label="פעולות">
                              {purchase.status === "PENDING" && (
                                <button
                                  className={styles.markPaidBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsPaid(purchase._id);
                                  }}
                                >
                                  סמן כשולם
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Load more */}
              {hasMore && purchases.length > 0 && (
                <div className={styles.loadMore}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <Spinner /> : "\u05D8\u05E2\u05DF \u05E2\u05D5\u05D3"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
